import React, { useEffect, useState } from "react";
import { Card, Form, Button, Table } from "react-bootstrap";
import { Season } from "../../types/model";
import {
	addSeasonToAllEvents,
	addSeasonToAllLeagues,
	addSeasonToAllTeams,
	createSeason,
	getSeasons,
	updateSeason,
} from "../../services/seasonService";
import AdminLayout from "./AdminLayout";

const AdminSeasonsPage: React.FC = () => {
	const [seasons, setSeasons] = useState<Season[]>([]);
	const [newSeason, setNewSeason] = useState<Season>({
		name: "",
		startDate: "",
		endDate: "",
		isActive: false,
	});
	const [editingSeasonId, setEditingSeasonId] = useState<string | null>(null);
	const [editingSeason, setEditingSeason] = useState<Season>({
		name: "",
		startDate: "",
		endDate: "",
		isActive: false,
	});

	useEffect(() => {
		fetchSeasons();
	}, []);

	const fetchSeasons = async () => {
		const res = await getSeasons();
		console.log(res);
		if (res) {
			setSeasons(res.data);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setNewSeason({ ...newSeason, [name]: value });
	};

	const handleCreateSeason = async () => {
		if (newSeason.name && newSeason.startDate && newSeason.endDate) {
			const res = await createSeason(newSeason);

			if (res) {
				console.log("Season created successfully:", res);
				setSeasons([...seasons, newSeason]);
			}
			setNewSeason({ name: "", startDate: "", endDate: "", isActive: false });
		}
	};

	const handleAddToEvents = async (seasonId: string) => {
		try {
			const res = await addSeasonToAllEvents(seasonId);
			if (res) {
				console.log("Season added to all events successfully:", res);
			}
		} catch (error) {
			console.error("Error adding season to all events:", error);
		}
	};

	const handleAddToLeagues = async (seasonId: string) => {
		try {
			const res = await addSeasonToAllLeagues(seasonId);
			if (res) {
				console.log("Season added to all leagues successfully:", res);
			}
		} catch (error) {
			console.error("Error adding season to all leagues:", error);
		}
	};

	const handleAddToTeams = async (seasonId: string) => {
		try {
			const res = await addSeasonToAllTeams(seasonId);
			if (res) {
				console.log("Season added to all teams successfully:", res);
			}
		} catch (error) {
			console.error("Error adding season to all teams:", error);
		}
	};

	const startEditingSeason = (season: Season) => {
		setEditingSeasonId(season._id ?? null);
		setEditingSeason({
			name: season.name,
			startDate: season.startDate,
			endDate: season.endDate,
			isActive: season.isActive,
			_id: season._id,
		});
	};

	const cancelEditingSeason = () => {
		setEditingSeasonId(null);
		setEditingSeason({
			name: "",
			startDate: "",
			endDate: "",
			isActive: false,
		});
	};

	const handleUpdateSeason = async () => {
		if (!editingSeasonId) return;
		try {
			const res = await updateSeason(editingSeasonId, {
				name: editingSeason.name,
				isActive: editingSeason.isActive,
			});
			if (res) {
				await fetchSeasons();
				cancelEditingSeason();
			}
		} catch (error) {
			console.error("Error updating season:", error);
			alert("Error updating season");
		}
	};

	return (
		<AdminLayout>
			<div className="container mt-4">
			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Create a New Season</Card.Title>
					<Form>
						<Form.Group className="mb-3">
							<Form.Label>Season Name</Form.Label>
							<Form.Control
								type="text"
								name="name"
								value={newSeason.name}
								onChange={handleInputChange}
								placeholder="Enter season name"
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>Start Date</Form.Label>
							<Form.Control
								type="date"
								name="startDate"
								value={newSeason.startDate}
								onChange={handleInputChange}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>End Date</Form.Label>
							<Form.Control
								type="date"
								name="endDate"
								value={newSeason.endDate}
								onChange={handleInputChange}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Check
								type="checkbox"
								label="Is Active"
								name="isActive"
								checked={newSeason.isActive}
								onChange={(e) =>
									setNewSeason({ ...newSeason, isActive: e.target.checked })
								}
							/>
						</Form.Group>
						<Button variant="primary" onClick={handleCreateSeason}>
							Create Season
						</Button>
					</Form>
				</Card.Body>
			</Card>

			<Card>
				<Card.Body>
					<Card.Title>All Seasons</Card.Title>
					<Table striped bordered hover>
						<thead>
							<tr>
								<th>#</th>
								<th>Name</th>
								<th>Start Date</th>
								<th>End Date</th>
								<th>Status</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{seasons.map((season, index) => {
								const isEditing = editingSeasonId === season._id;
								return (
									<tr key={season._id ?? index}>
										<td>{index + 1}</td>
										<td>
											{isEditing ? (
												<Form.Control
													type="text"
													value={editingSeason.name}
													onChange={(e) =>
														setEditingSeason({
															...editingSeason,
															name: e.target.value,
														})
													}
												/>
											) : (
												season.name
											)}
										</td>
										<td>{season.startDate}</td>
										<td>{season.endDate}</td>
										<td>
											{isEditing ? (
												<Form.Check
													type="switch"
													checked={editingSeason.isActive}
													onChange={(e) =>
														setEditingSeason({
															...editingSeason,
															isActive: e.target.checked,
														})
													}
													label={editingSeason.isActive ? "Active" : "Inactive"}
												/>
											) : season.isActive ? (
												"Active"
											) : (
												"Inactive"
											)}
										</td>
										<td>
											{isEditing ? (
												<>
													<Button
														variant="primary"
														size="sm"
														className="me-2"
														onClick={handleUpdateSeason}
													>
														Save
													</Button>
													<Button
														variant="outline-secondary"
														size="sm"
														onClick={cancelEditingSeason}
													>
														Cancel
													</Button>
												</>
											) : (
												<>
													<Button
														variant="outline-primary"
														size="sm"
														className="me-2"
														onClick={() => startEditingSeason(season)}
													>
														Edit
													</Button>
													<Button
														variant="secondary"
														size="sm"
														className="me-2"
														onClick={() => handleAddToEvents(season._id!)}
													>
														Add to Events
													</Button>
													<Button
														variant="secondary"
														size="sm"
														onClick={() => handleAddToLeagues(season._id!)}
													>
														Add to Leagues
													</Button>
													<Button
														variant="secondary"
														size="sm"
														className="ms-2"
														onClick={() => handleAddToTeams(season._id!)}
													>
														Add to Teams
													</Button>
												</>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			</div>
		</AdminLayout>
	);
};

export default AdminSeasonsPage;
